import { alpha, Box, Chip, Stack, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { get, isEmpty } from 'lodash';

// Components
import FlagCountry from 'src/components/common/FlagCountry';
import { UnknownText } from 'src/utils/constants/config';
import {
  checkCountryHaveState,
  convertToStartCase,
  getCountryByCountryCode,
  renderTextOrDefault,
} from 'src/utils/helpers/stringHelpers';
import { NATIONALITIES } from 'src/utils/constants/nationality';
import CheckedCredit from 'src/components/Icons/CheckedCredit';
import LogoCompany from 'src/components/common/LogoCompany';
import ButtonBookmark from 'src/components/common/ButtonBookmark';

// Utilities

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `2px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius * 2,
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

interface IProps {
  people: IPeopleProfile;
  selected?: IPeopleProfile;
  onSelect: (people: IPeopleProfile) => void;
  handleOpenBookmarkNoteForm: (companyId: string, companyHasBookmark: boolean) => void;
  isBookmark: boolean;
}

const PeopleProfileCard = ({ people, selected, isBookmark, onSelect, handleOpenBookmarkNoteForm }: IProps) => {
  const theme = useTheme();

  const isSelected = selected?.id === people.id ? true : false;
  const countryCode = people?.country_code || get(NATIONALITIES, people?.nationality || '', '');
  const country = getCountryByCountryCode(countryCode || '');
  const isCountryHaveState = checkCountryHaveState(country?.code);

  return (
    <StyledBox
      onClick={() => onSelect(people)}
      sx={{
        borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
        cursor: 'pointer',
      }}
      height="100%"
    >
      <Box width="100%">
        <Stack direction="column" spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <Stack width="90%" direction="row" spacing={1} alignItems="center">
              <Tooltip placement="top-start" title={renderTextOrDefault(people?.full_name)}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: '1',
                    lineClamp: '1',
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {renderTextOrDefault(people?.full_name)}
                </Typography>
              </Tooltip>
              {people?.is_alerts && (
                <Chip
                  label="Alerts"
                  color="warning"
                  size="small"
                  variant="filled"
                  sx={{
                    height: 18,
                    color: theme.palette.common.white,
                    backgroundColor: theme.palette.warning.main,
                    '&:hover': {
                      color: theme.palette.common.white,
                      backgroundColor: theme.palette.warning.main,
                    },
                  }}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {people?.check_entity_id_type_created_30_days_result && <CheckedCredit fontSize="small" />}{' '}
              <ButtonBookmark
                iconProps={{ sx: { fontSize: 12 } }}
                buttonProps={{
                  size: 'medium',
                  onClick: (event) => {
                    event.stopPropagation();
                    handleOpenBookmarkNoteForm(people?.id || '', isBookmark || false);
                  },
                }}
                isMarked={isBookmark}
              />
            </Stack>
          </Stack>
          {people?.position && (
            <Typography variant="caption2" color="text.secondary" align="left">
              {convertToStartCase(people?.position || UnknownText)}
            </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <FlagCountry width={20} code={country.code || ''} />
            {isCountryHaveState ? (
              <Typography variant="caption2" color={'text.secondary'}>
                {country.code} {people?.jurisdiction ? `- ${convertToStartCase(people?.jurisdiction)}` : ''}
              </Typography>
            ) : (
              <Typography variant="caption2" color={'text.secondary'}>
                {convertToStartCase(country.label)}
              </Typography>
            )}
          </Stack>
          {people?.company_name && (
            <Stack direction="row" spacing={1} alignItems="center">
              <LogoCompany logoURL={''} width={16} height={16} />
              <Tooltip placement="top-start" title={(people?.company_name || UnknownText).toUpperCase()}>
                <Typography
                  variant="caption2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: '1',
                    lineClamp: '1',
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {(people?.company_name || UnknownText).toUpperCase()}
                </Typography>
              </Tooltip>
            </Stack>
          )}
          {!isEmpty(people.category) && (
            <Stack gap={0.5} direction="row" flexWrap="wrap">
              {people.category.map((item) => (
                <Chip
                  key={`category-${people.id}-${item}`}
                  label={item}
                  color="secondary"
                  size="small"
                  sx={{
                    '&:hover': {
                      color: theme.palette.secondary.main,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                    },
                  }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Box>
    </StyledBox>
  );
};

export default PeopleProfileCard;
